/**
 * 사례 편집 — 이름 바꾸기(변조) 패널(사용자 요청 2026-10-11: 「실제 테이블·컬럼명을 AI 로 변조(영문 → 한글명)」).
 *
 * AI 는 **매핑(원래 이름 → 바꿀 이름)만 제안**하고, 바꾸기는 「양식에 적용」 이 글자 그대로 한다(utils/caseRedact).
 * 모델에게 사례 글을 다시 쓰게 하면 실행계획 표·수치·SQL 이 조용히 틀어진다. 매핑 표는 사람이 고치고 더할 수 있어
 * AI 없이도 끝까지 된다(가이드라인 원칙 2). 「다시 제안」 은 지금 매핑 + 지시를 함께 보낸다(원칙 4). 중지는 요청을 끊는다(원칙 3).
 * 서버가 버린 제안은 사유와 함께 보인다. 공개 사례는 매핑이 변조 검증 근거로 저장되고(주인만 본다), 내부 사례에는 매핑을
 * 저장하지 않는다(원래 이름이 남으면 바꾼 의미가 없다) — 바꾼 건수만 저장 이력에 간다(서버가 문장을 쓴다).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiCpu, FiPlus, FiX, FiCheck, FiRotateCcw, FiSquare, FiBookmark } from 'react-icons/fi';
import { suggestCaseRedaction, getRenameRules, getRenameRule, createRenameRule } from '../../services/assistantClient';
import { applyMapping, leftovers, relatedLeft, cleanRows, rowsInCase } from '../../utils/caseRedact';
import { usePrompt } from '../PromptDialog';
import { formatApiError } from '../../utils/formatters';
import { alignPipeTables } from '../../utils/planAlign';

function CaseRedactPanel({ form, onApplyForm, rows, setRows, provider, ruleFolder, ruleId, setRuleId }) {
    const { t } = useTranslation();
    const prompt = usePrompt();
    /**
     * 매핑 규칙(원본 폴더 단위 누적, 2026-10-11) — 탭을 열면 원본 폴더의 기본 규칙을 고르고, 이 사례 글에 나오는 이름만 표에 올린다.
     * 다른 규칙(다른 폴더 것 포함)을 고르면 그 규칙으로 갈아 끼운다(사람·AI 가 적은 행은 그대로). 저장하면 적용한 매핑이 고른 규칙에 누적된다.
     */
    const [rules, setRules] = useState([]);
    const [ruleInfo, setRuleInfo] = useState(null);   // { total, used }
    const [ruleError, setRuleError] = useState(null);
    const loadRule = useCallback(async (id) => {
        if (!id) { setRows(prev => prev.filter(x => x.origin !== 'rule')); setRuleInfo(null); return; }
        try {
            const r = await getRenameRule(id);
            const entries = Object.entries((r.rule && r.rule.mapping) || {}).map(([from, to]) => ({ from, to }));
            const hit = rowsInCase(form, entries);
            setRows(prev => {
                const keep = prev.filter(x => x.origin !== 'rule');
                const taken = new Set(keep.map(x => String(x.from || '').trim().toUpperCase()));
                return [...keep, ...hit.filter(e => !taken.has(e.from.toUpperCase())).map(e => ({ ...e, origin: 'rule' }))];
            });
            setRuleInfo({ total: entries.length, used: hit.length });
            setRuleError(null);
        } catch (err) { setRuleError(formatApiError(err, t)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 규칙을 고를 때의 양식으로 고른다(양식을 고칠 때마다 다시 불러오지 않는다)
    }, [setRows, t]);
    useEffect(() => {
        let alive = true;
        // Promise.resolve 로 감싼다 — 규칙을 못 불러와도(오류·빈 응답) 탭은 그대로 쓸 수 있어야 한다
        Promise.resolve().then(() => getRenameRules(ruleFolder || '')).then((r) => {
            if (!alive) return;
            const list = (r && r.rules) || [];
            setRules(list);
            const pick = ruleId && list.some(x => x.id === ruleId) ? ruleId
                : ((list.find(x => x.folder === (ruleFolder || '')) || {}).id || null);
            if (pick && pick !== ruleId) setRuleId(pick);
            if (pick) loadRule(pick);
        }).catch(err => { if (alive) setRuleError(formatApiError(err, t)); });
        return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 탭을 열 때 한 번
    }, []);
    const pickRule = (id) => { setRuleId(id || null); loadRule(id); };
    const newRule = async () => {
        const name = await prompt({
            title: t('assistant:caseRule.newTitle'),
            message: t('assistant:caseRule.newHint', { folder: ruleFolder || t('assistant:caseRule.noFolder') }),
            confirmText: t('assistant:caseRule.create'),
        });
        if (!name || !String(name).trim()) return;
        try {
            const r = await createRenameRule({ folder: ruleFolder || '', name: String(name).trim() });
            setRules(prev => [...prev, { ...r.rule, mapping: undefined }]);
            pickRule(r.rule.id);
        } catch (err) { setRuleError(formatApiError(err, t)); }
    };
    const ruleLabel = (r) => `${r.folder || t('assistant:caseRule.noFolder')} · ${r.name} (${r.count})`;
    const [instruction, setInstruction] = useState('');
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);      // { added, rejected, usage, candidates }
    const [error, setError] = useState(null);
    const [applied, setApplied] = useState(null);    // { total, left, related, prevForm }
    const stopRef = useRef(null);

    const suggest = useCallback(async () => {
        const ctl = new AbortController();
        stopRef.current = ctl;
        setRunning(true); setError(null); setResult(null);
        try {
            const r = await suggestCaseRedaction({
                fields: form, instruction: instruction.trim(), provider,
                current: cleanRows(rows),
            }, ctl.signal);
            // 사람이 적은 행·규칙에서 온 행은 남기고, 같은 원래 이름이면 그것을 앞세운다 — 다시 제안이 고친 것·규칙을 덮지 않게
            const mine = rows.filter(x => x.origin !== 'ai' && String(x.from || '').trim());
            const taken = new Set(mine.map(x => x.from.trim().toUpperCase()));
            const fromAi = (r.mapping || []).filter(m => !taken.has(m.from.toUpperCase())).map(m => ({ ...m, origin: 'ai' }));
            setRows([...mine, ...fromAi]);
            setResult({ added: fromAi.length, rejected: r.rejected || [], usage: r.usage || {}, candidates: r.candidates });
        } catch (err) {
            if (ctl.signal.aborted) setError(t('assistant:caseRedact.stopped'));
            else setError(formatApiError(err, t));
        } finally { setRunning(false); stopRef.current = null; }
    }, [form, instruction, provider, rows, setRows, t]);

    const stop = () => { if (stopRef.current) stopRef.current.abort(); };

    const apply = () => {
        const r = applyMapping(form, rows);
        // 이름 길이가 바뀌면 실행계획 표의 칸이 밀린다 — 바뀐 계획만 화면 폭 기준(한글 2칸)으로 다시 맞춘다(사용자 신고 2026-10-11)
        for (const k of ['beforePlan', 'afterPlan']) {
            if (r.fields[k] && r.fields[k] !== form[k]) r.fields[k] = alignPipeTables(r.fields[k]);
        }
        const used = cleanRows(rows).map(x => x.from.toUpperCase());
        const ai = rows.filter(x => x.origin === 'ai' && used.includes(String(x.from || '').trim().toUpperCase())).length;
        // 건수만 저장 이력으로 간다(이름은 보내지 않는다 — 내부 사례에는 원래 이름을 남기지 않는다)
        onApplyForm(r.fields, { names: used.length, places: r.total, ai });
        setApplied({ total: r.total, left: leftovers(r.fields, rows), related: relatedLeft(r.fields, rows), prevForm: form });
        toast.success(t('assistant:caseRedact.applied', { count: r.total }));
    };
    const undo = () => {
        if (!applied) return;
        onApplyForm(applied.prevForm, null);
        setApplied(null);
    };

    const setRow = (i, key, value) => setRows(prev => prev.map((x, j) => (j === i ? { ...x, [key]: value, origin: 'user' } : x)));
    const usable = cleanRows(rows).length;
    const cost = result && result.usage && Number.isFinite(Number(result.usage.costUsd)) ? Number(result.usage.costUsd) : null;

    return (
        <div className="tcf-redact">
            <div className="tcf-label">{t('assistant:caseRedact.title')}</div>
            <div className="tcf-hint">{t('assistant:caseRedact.hint')}</div>
            {/* 매핑 규칙 — 원본 폴더 단위로 누적. 고르면 이 사례에 나오는 이름만 표에 올라온다 */}
            <div className="tcf-redactBar tcf-ruleBar">
                <FiBookmark size={12} />
                <span>{t('assistant:caseRule.label')}</span>
                <select className="tcf-select" value={ruleId || ''} onChange={(e) => pickRule(e.target.value)} disabled={running}>
                    <option value="">{t('assistant:caseRule.none')}</option>
                    {rules.map(r => <option key={r.id} value={r.id}>{ruleLabel(r)}</option>)}
                </select>
                <button type="button" className="tcf-btn" onClick={newRule} disabled={running}><FiPlus size={11} /> {t('assistant:caseRule.new')}</button>
                {ruleInfo && <span className="tcf-hint tcf-aiUse">{t('assistant:caseRule.info', { used: ruleInfo.used, total: ruleInfo.total })}</span>}
            </div>
            {ruleId && <div className="tcf-hint">{t('assistant:caseRule.accumulate')}</div>}
            {ruleError && <div className="tcf-redactErr" role="alert">{ruleError}</div>}
            <div className="tcf-redactBar">
                <input
                    className="tcf-input" value={instruction} onChange={(e) => setInstruction(e.target.value)}
                    placeholder={t('assistant:caseRedact.instructionPh')} disabled={running}
                />
                {running ? (
                    <button type="button" className="tcf-btn" onClick={stop}><FiSquare size={11} /> {t('assistant:caseRedact.stop')}</button>
                ) : (
                    <button type="button" className="tcf-btn" onClick={suggest} title={t('assistant:caseRedact.aiUse')}>
                        <FiCpu size={11} /> {t(rows.some(x => x.origin === 'ai') ? 'assistant:caseRedact.resuggest' : 'assistant:caseRedact.suggest')}
                    </button>
                )}
                <span className="tcf-hint tcf-aiUse">{t('assistant:caseRedact.aiUse')}</span>
            </div>
            {running && <div className="tcf-hint">{t('assistant:caseRedact.running')}</div>}
            {error && <div className="tcf-redactErr" role="alert">{error}</div>}
            {result && (
                <div className="tcf-hint tcf-fade">
                    {t('assistant:caseRedact.result', { added: result.added, rejected: result.rejected.length, candidates: result.candidates || 0 })}
                    {cost !== null ? ` · ${t('assistant:caseRedact.cost', { cost: cost.toFixed(4) })}` : ''}
                </div>
            )}
            {result && result.rejected.length > 0 && (
                <details className="tcf-redactRejected">
                    <summary>{t('assistant:caseRedact.rejectedTitle', { count: result.rejected.length })}</summary>
                    <ul>
                        {result.rejected.map((r, i) => (
                            // eslint-disable-next-line react/no-array-index-key -- 서버가 준 순서가 곧 키다
                            <li key={i}><code>{r.from}</code> → <code>{r.to || '-'}</code> · {t(`assistant:caseRedact.reason.${r.reason}`)}</li>
                        ))}
                    </ul>
                </details>
            )}

            {rows.map((r, i) => (
                // eslint-disable-next-line react/no-array-index-key -- 행에 고유 id 가 없다. 값으로 키를 만들면 타이핑 중 포커스가 튄다
                <div className="tcf-redRow" key={i}>
                    <input className="tcf-input tcf-mono" value={r.from} placeholder={t('assistant:caseForm.redFrom')}
                        onChange={(e) => setRow(i, 'from', e.target.value)} />
                    <span className="tcf-arrow">→</span>
                    <input className="tcf-input tcf-mono" value={r.to} placeholder={t('assistant:caseForm.redTo')}
                        onChange={(e) => setRow(i, 'to', e.target.value)} />
                    <span className={`tcf-origin tcf-origin-${r.origin === 'ai' || r.origin === 'rule' ? r.origin : 'user'}`}>
                        {t(r.origin === 'ai' ? 'assistant:caseRedact.originAi' : (r.origin === 'rule' ? 'assistant:caseRedact.originRule' : 'assistant:caseRedact.originUser'))}
                    </span>
                    <button type="button" className="tcf-iconBtn" title={t('common:ui.delete')}
                        onClick={() => setRows(prev => prev.filter((_x, j) => j !== i))}>
                        <FiX size={11} />
                    </button>
                </div>
            ))}
            <div className="tcf-redactBar">
                <button type="button" className="tcf-btn" onClick={() => setRows(prev => [...prev, { from: '', to: '', origin: 'user' }])}>
                    <FiPlus size={11} /> {t('assistant:caseForm.redAdd')}
                </button>
                <button type="button" className="tcf-btn tcf-primary" disabled={!usable || running} onClick={apply}>
                    <FiCheck size={11} /> {t('assistant:caseRedact.apply', { count: usable })}
                </button>
                {applied && (
                    <button type="button" className="tcf-btn" onClick={undo}><FiRotateCcw size={11} /> {t('assistant:caseRedact.undo')}</button>
                )}
            </div>
            {applied && (applied.left.length > 0 || applied.related.length > 0) && (
                <div className="tcf-redactWarn tcf-fade" role="status">
                    {applied.left.length > 0 && <div>{t('assistant:caseRedact.left', { names: applied.left.join(', ') })}</div>}
                    {applied.related.length > 0 && <div>{t('assistant:caseRedact.related', { names: applied.related.join(', ') })}</div>}
                </div>
            )}
        </div>
    );
}

CaseRedactPanel.propTypes = {
    form: PropTypes.objectOf(PropTypes.string).isRequired,
    onApplyForm: PropTypes.func.isRequired,
    rows: PropTypes.arrayOf(PropTypes.shape({ from: PropTypes.string, to: PropTypes.string, origin: PropTypes.string })).isRequired,
    setRows: PropTypes.func.isRequired,
    provider: PropTypes.string,
    /** 매핑 규칙의 원본 폴더(복제본이면 원본 사례의 폴더) */
    ruleFolder: PropTypes.string,
    ruleId: PropTypes.string,
    setRuleId: PropTypes.func.isRequired,
};

export default CaseRedactPanel;
